import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import Drawer from "@intility/bifrost-react/Drawer";
import Grid from "@intility/bifrost-react/Grid";
import Icon from "@intility/bifrost-react/Icon";
import Inline from "@intility/bifrost-react/Inline";
import Message from "@intility/bifrost-react/Message";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createResourceType,
  createUsage,
  deleteUsage,
  getBlueprints,
  listResourceTypes,
  updateUsage,
  validateUsagePlacement,
} from "~/api/client";
import type {
  BlueprintNode,
  BlueprintResponse,
  CreateResourceTypeRequest,
  PlatformName,
  ResourceType,
} from "~/api/fga-types";
import BlueprintCanvas, { type BlueprintSelection } from "./BlueprintCanvas";
import BlueprintDrawer from "./BlueprintDrawer";
import BlueprintReview from "./BlueprintReview";

function findNode(
  nodes: BlueprintNode[],
  usageId: string,
): BlueprintNode | null {
  for (const n of nodes) {
    if (n.usageId === usageId) return n;
    const found = findNode(n.children, usageId);
    if (found) return found;
  }
  return null;
}

export interface BlueprintEditorProps {
  /** Display names for the canvas, keyed on Platform slug. */
  platforms: PlatformName[];
  /** Bare Platform slug (e.g. `apm`) whose blueprint to edit. */
  activePlatform: string;
}

/**
 * Interactive blueprint editor — the same canvas + drawer setup as the build
 * phase of the Blueprint Builder, scoped to a single platform. Loads its own
 * data so it can be dropped into any platform context (e.g. the platform
 * detail page's edit mode).
 */
export default function BlueprintEditor({
  platforms,
  activePlatform,
}: BlueprintEditorProps) {
  const [blueprint, setBlueprint] = useState<BlueprintResponse | null>(null);
  const [types, setTypes] = useState<ResourceType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selection, setSelection] = useState<BlueprintSelection | null>(null);
  // "inspect" = node clicked (view/edit/delete); "add" = "+" clicked (add child).
  const [drawerMode, setDrawerMode] = useState<"inspect" | "add">("inspect");

  const loadAll = useCallback(async () => {
    setError(null);
    try {
      const [bp, ts] = await Promise.all([
        getBlueprints(),
        listResourceTypes(),
      ]);
      setBlueprint(bp);
      setTypes(ts.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSelect = (id: string | null, sel: BlueprintSelection | null) => {
    setSelectedId(id);
    setSelection(sel);
    setDrawerMode("inspect");
  };

  const handleAddChild = (_id: string, sel: BlueprintSelection | null) => {
    // "+" is an add action, not a selection — don't outline the node. The
    // drawer's "Add to X" header conveys which node we're adding under.
    setSelectedId(null);
    setSelection(sel);
    setDrawerMode("add");
  };

  const closeDrawer = () => {
    setSelectedId(null);
    setSelection(null);
  };

  const siblingTypeIdsFor = useCallback(
    (platformSlug: string, parentUsageId: string | null): Set<string> => {
      const p = blueprint?.platforms.find((pf) => pf.platform === platformSlug);
      if (!p) return new Set();
      const siblings =
        parentUsageId === null
          ? p.tree
          : (findNode(p.tree, parentUsageId)?.children ?? []);
      return new Set(siblings.map((n) => n.type.id));
    },
    [blueprint],
  );

  const availableTypes = useMemo<ResourceType[]>(() => {
    if (!selection) return [];
    if (selection.kind === "platform") {
      const siblings = siblingTypeIdsFor(selection.platformSlug, null);
      return types.filter((t) => !siblings.has(t.id));
    }
    if (selection.kind === "resource") {
      const siblings = siblingTypeIdsFor(
        selection.platformSlug,
        selection.usageId,
      );
      return types.filter((t) => !siblings.has(t.id));
    }
    return [];
  }, [selection, siblingTypeIdsFor, types]);

  const drawerOpen = selection !== null;

  const handleAddUsage = async (
    platformSlug: string,
    parentUsageId: string | null,
    typeId: string,
    qualifiedName?: string,
  ) => {
    setBusy(true);
    setError(null);
    try {
      const type = types.find((t) => t.id === typeId);
      await createUsage(platformSlug, {
        typeId,
        parentUsageId: parentUsageId ?? undefined,
        qualifiedName: qualifiedName || type?.slug || typeId,
      });
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleUpdateUsage = async (usageId: string, qualifiedName: string) => {
    setBusy(true);
    setError(null);
    try {
      await updateUsage(usageId, { qualifiedName });
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteUsage = async (usageId: string) => {
    setBusy(true);
    setError(null);
    try {
      await deleteUsage(usageId);
      closeDrawer();
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleCreateType = async (
    req: CreateResourceTypeRequest,
  ): Promise<ResourceType | undefined> => {
    setBusy(true);
    setError(null);
    try {
      const newType = await createResourceType(req);
      await loadAll();
      return newType;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  // Prechecks a placement for a not-yet-created type before it's created, so a
  // placement that would be rejected never leaves an orphan type behind.
  const handleValidatePlacement = async (
    platformSlug: string,
    parentUsageId: string | null,
    qualifiedName: string,
    typeNameRegex: string | undefined,
    typeIsStatic: boolean,
  ) => {
    setBusy(true);
    setError(null);
    try {
      await validateUsagePlacement(platformSlug, {
        parentUsageId,
        qualifiedName,
        typeNameRegex,
        typeIsStatic,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    } finally {
      setBusy(false);
    }
  };

  if (!blueprint) {
    return (
      <Grid gap={16} style={{ minHeight: 100, placeContent: "center" }}>
        <Icon.Spinner size={24} />
      </Grid>
    );
  }

  return (
    <>
      {error && (
        <Message state="alert" header="Error" style={{ margin: "0 0 12px" }}>
          {error}
        </Message>
      )}

      <div
        style={{
          position: "relative",
          height: 600,
          padding: 4,
          border: "2px dashed var(--bfc-theme)",
          borderRadius: 8,
        }}
      >
        <Inline
          align="center"
          gap={8}
          style={{
            position: "absolute",
            top: 12,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 5,
            padding: "2px 12px",
            borderRadius: 999,
            background: "var(--bfc-base-2)",
            border: "1px solid var(--bfc-theme)",
          }}
        >
          <Icon icon={faPencil} style={{ color: "var(--bfc-theme)" }} />
          <strong style={{ color: "var(--bfc-theme)" }}>Edit mode</strong>
        </Inline>
        <BlueprintCanvas
          blueprint={blueprint}
          platforms={platforms}
          selectedId={selectedId}
          onSelect={handleSelect}
          singlePlatform={activePlatform}
          onAddChild={handleAddChild}
          enableAddButton
        />
      </div>

      {/* An empty blueprint is valid, so the list view stays up while it has
          no placed resources; it fills in as resources are added. */}
      <div style={{ marginTop: 24 }}>
        <h2 className="bf-h3" style={{ marginBottom: 12 }}>
          List view
        </h2>
        <BlueprintReview
          blueprint={blueprint}
          platforms={platforms}
          singlePlatform={activePlatform}
          hidePlatformTitle
          fullWidth
        />
      </div>

      <Drawer
        header={
          selection
            ? drawerMode === "add"
              ? `Add to ${
                  selection.kind === "platform"
                    ? selection.displayName
                    : selection.kind === "resource"
                      ? selection.typeName
                      : "company"
                }`
              : selection.kind === "company"
                ? "Company"
                : selection.kind === "platform"
                  ? "Platform"
                  : "Resource"
            : undefined
        }
        isOpen={drawerOpen}
        onRequestClose={closeDrawer}
      >
        <BlueprintDrawer
          selection={selection}
          availableTypes={availableTypes}
          busy={busy}
          mode={drawerMode}
          onAddUsage={handleAddUsage}
          onUpdateUsage={handleUpdateUsage}
          onDeleteUsage={handleDeleteUsage}
          onCreateType={handleCreateType}
          onValidatePlacement={handleValidatePlacement}
        />
      </Drawer>
    </>
  );
}
